import { useState } from 'react';
import {
  MOVE_ATTACHMENTS_IMAGE_EXT,
  SOURCE_SYSTEMS,
  SOURCE_SYSTEM_LABELS,
  buildMoveAttachmentsPaths,
  isMoveAttachmentsReady,
  moveAttachmentsSourcePathError,
  parseMoveAttachmentsSource,
  type MoveAttachmentsJob,
  type SourceSystem,
} from '@atlas/shared';
import { createMoveAttachments } from '../lib/callables.js';

type CopyChoice = 'documents' | 'both' | 'images';

const COPY_CHOICES: Array<{ id: CopyChoice; title: string; detail: string }> = [
  { id: 'documents', title: 'Documents', detail: 'all_files/' },
  { id: 'both', title: 'Documents and images', detail: 'all_files/ and all_images/' },
  { id: 'images', title: 'Images', detail: 'all_images/' },
];

interface NewMoveAttachmentsFormProps {
  existingJobs?: Array<MoveAttachmentsJob & { id: string }>;
}

function describeError(err: unknown): string {
  const message = err instanceof Error ? err.message : 'Unknown error';
  if (/permission-denied|not authorised|do not have access/i.test(message)) {
    return 'You do not have access to this app. Ask a platform admin to grant you the redshift-export app.';
  }
  return message;
}

export function NewMoveAttachmentsForm({ existingJobs = [] }: NewMoveAttachmentsFormProps) {
  const [sourceSystem, setSourceSystem] = useState<SourceSystem | ''>('');
  const [sourcePath, setSourcePath] = useState('');
  const [copyChoice, setCopyChoice] = useState<CopyChoice>('documents');
  const [archivePassword, setArchivePassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const isBullhorn = sourceSystem === 'bullhorn';
  const isThrive = sourceSystem === 'thrive';
  const paths = buildMoveAttachmentsPaths(sourcePath);
  const identity = parseMoveAttachmentsSource(sourcePath);
  const pathProblem = sourcePath.trim() ? moveAttachmentsSourcePathError(sourcePath) : null;
  // Bullhorn always copies both document trees (people/ and companies/).
  const imagesOnly = !isBullhorn && copyChoice === 'images';
  const copyImages = isBullhorn || copyChoice !== 'documents';
  const bullhornNeedsPassword = isBullhorn && !archivePassword.trim();
  const watched = paths
    ? [
        ...(imagesOnly ? [] : [paths.destPrefix]),
        ...(copyImages ? [paths.imageDestPrefix] : []),
      ]
    : [];
  const ready = sourceSystem !== '' && isMoveAttachmentsReady(sourceSystem);
  const sourceLabel = sourceSystem ? SOURCE_SYSTEM_LABELS[sourceSystem] : null;
  const busy = existingJobs.some(
    (job) =>
      (job.status === 'queued' ||
        job.status === 'running' ||
        job.status === 'awaiting_confirm') &&
      watched.some((prefix) =>
        (!job.imagesOnly && prefix === job.destPrefix) ||
        ((job.imagesOnly || job.includeImages) && prefix === job.imageDestPrefix),
      ),
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!paths || busy || !ready || identity?.sourceSystem !== sourceSystem) return;
    if (bullhornNeedsPassword) return;
    setError(null);
    setLastResult(null);
    setLoading(true);
    try {
      const result = await createMoveAttachments({
        sourcePath: sourcePath.trim(),
        includeImages: copyImages,
        imagesOnly,
        ...(isBullhorn ? { archivePassword: archivePassword.trim() } : {}),
      });
      const landing = [imagesOnly ? null : result.data.destUri, result.data.imageDestUri]
        .filter(Boolean)
        .join(' and ');
      setLastResult(
        `Dry run started for ${identity?.firm ?? 'this agency'}. ${
          isBullhorn ? 'The archives are extracted first, then files' : 'Files'
        } will land in ${landing} after you confirm the inventory.`,
      );
      setSourcePath('');
      setArchivePassword('');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-4 p-4 border border-[#e9e9e7] rounded-lg bg-white"
    >
      <div>
        <h2 className="text-sm font-semibold text-[#37352f]">New run</h2>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-[#73726e]">Source system</span>
        <select
          required
          className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] bg-white"
          value={sourceSystem}
          onChange={(event) => {
            const next = event.target.value as SourceSystem;
            setSourceSystem(next);
            setSourcePath('');
            // Thrive populates both all_files/ and all_images/, so default to both.
            setCopyChoice(next === 'thrive' ? 'both' : 'documents');
            setArchivePassword('');
            setError(null);
            setLastResult(null);
          }}
        >
          <option value="" disabled>
            Select source system…
          </option>
          {SOURCE_SYSTEMS.map((source) => (
            <option key={source} value={source}>
              {SOURCE_SYSTEM_LABELS[source]}
              {isMoveAttachmentsReady(source) ? '' : ' — not set up'}
            </option>
          ))}
        </select>
      </label>

      {sourceSystem !== '' && !ready && sourceLabel && (
        <p className="text-sm text-[#73726e] bg-[#f7f7f5] border border-[#e9e9e7] rounded px-3 py-2">
          {sourceLabel} is not set up yet. Its export arrives in a different layout, so it will
          get its own steps.
        </p>
      )}

      {ready && (
        <>
      <p className="text-xs text-[#73726e]">
        {sourceSystem === 'jobadder' ? (
          <>
            Reads <span className="font-mono">Backup/unzipped/</span>. Month folders such as{' '}
            <span className="font-mono">202401/</span> are kept. Documents land in{' '}
            <span className="font-mono">all_files/</span> and images in{' '}
            <span className="font-mono">all_images/</span>, still inside their month folder.
            The same extensions as Vincere are copied. A shared destination name keeps one file;
            the rest are listed in a log. A file already at the destination is skipped, so a
            stopped run can be repeated.
          </>
        ) : isBullhorn ? (
          <>
            Reads every encrypted <span className="font-mono">.7z</span> attachment archive in{' '}
            <span className="font-mono">files/</span>. They are downloaded and unzipped into{' '}
            <span className="font-mono">files/unzipped/</span> first, then the trees are copied
            keeping their <span className="font-mono">&lt;id&gt;/&lt;YYYY.MM&gt;/</span> folders:
            people attachments into <span className="font-mono">people/</span>, company attachments
            into <span className="font-mono">companies/</span>, and certifications into{' '}
            <span className="font-mono">certifications/</span>. A file already at the destination is
            skipped, so a stopped run can be repeated. After a verified copy the unzipped files are
            deleted.
          </>
        ) : isThrive ? (
          <>
            Reads <span className="font-mono">unzipped/&lt;export&gt;/&lt;firm&gt;/docs/</span>.
            Every folder inside <span className="font-mono">docs/</span> is kept as-is: documents
            land in <span className="font-mono">all_files/</span> and images in{' '}
            <span className="font-mono">all_images/</span>, preserving their structure (e.g.{' '}
            <span className="font-mono">all_images/avatars/1/…</span>). A shared destination path
            keeps one file; the rest are listed in a log. A file already at the destination is
            skipped, so a stopped run can be repeated.
          </>
        ) : (
          <>
            Reads <span className="font-mono">documents/unzipped/</span> in every date folder under{' '}
            <span className="font-mono">export_data/</span>. Documents land flat in{' '}
            <span className="font-mono">all_files/</span>. Images, when you ask for them, land in{' '}
            <span className="font-mono">all_images/</span>. A shared name keeps one file. The rest
            are listed in a log.
          </>
        )}
      </p>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-[#73726e]">Source path</span>
        <input
          required
          className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4] font-mono"
          placeholder={
            sourceSystem === 'jobadder'
              ? 's3://migration-sourcedatabases/jobadder/rata_recruitment/files'
              : isBullhorn
                ? 's3://migration-sourcedatabases/bullhorn/acme/files'
                : isThrive
                  ? 's3://migration-sourcedatabases/thrive/jackson_lombard/files'
                  : 's3://migration-sourcedatabases/vincere/ec3/files'
          }
          value={sourcePath}
          onChange={(event) => setSourcePath(event.target.value)}
        />
        <span className="text-xs text-[#aeaaa4]">
          The agency is the folder between the source system and{' '}
          <span className="font-mono">files</span>.
          {sourceSystem === 'jobadder'
            ? ' Date folders are found under Backup/unzipped/, so you do not type a date.'
            : isBullhorn
              ? ' The .7z archives sit directly in files/, so you do not type a date.'
              : isThrive
                ? ' The export and docs folders are found under unzipped/, so you do not type a date.'
                : ' Date folders are found from there, so you do not type a date.'}
        </span>
      </label>

      {isBullhorn && (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-[#73726e]">7-Zip password</span>
          <input
            required
            type="password"
            autoComplete="off"
            className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
            placeholder="Password for the .7z archives"
            value={archivePassword}
            onChange={(event) => setArchivePassword(event.target.value)}
          />
          <span className="text-xs text-[#aeaaa4]">
            Used to unzip both archives. Stored as a secret, never shown on the run.
          </span>
        </label>
      )}

      {identity && identity.sourceSystem === sourceSystem && sourceLabel && (
        <p className="text-xs text-[#73726e]">
          {sourceLabel}, agency <span className="font-mono text-[#37352f]">{identity.firm}</span>
        </p>
      )}
      {identity && identity.sourceSystem !== sourceSystem && sourceLabel && (
        <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">
          That path is {SOURCE_SYSTEM_LABELS[identity.sourceSystem as SourceSystem] ?? identity.sourceSystem}, not {sourceLabel}.
        </p>
      )}

      {isBullhorn ? (
        <p className="text-xs text-[#73726e]">
          All three document trees (people, companies, certifications) are copied. You pick which
          file types after the dry run.
        </p>
      ) : (
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-xs font-medium text-[#73726e] mb-1">What to copy</legend>
        <div className="flex flex-col rounded border border-[#e9e9e7] overflow-hidden">
          {COPY_CHOICES.map((choice) => {
            const selected = copyChoice === choice.id;
            return (
              <label
                key={choice.id}
                className={`flex items-center gap-2 px-2.5 py-2 cursor-pointer border-t border-[#e9e9e7] first:border-t-0 ${
                  selected ? 'bg-[#f7f7f5]' : 'hover:bg-[#fafafa]'
                }`}
              >
                <input
                  type="radio"
                  name="copy-choice"
                  className="accent-[#37352f]"
                  checked={selected}
                  onChange={() => setCopyChoice(choice.id)}
                />
                <span className="text-sm text-[#37352f]">{choice.title}</span>
                <span className="ml-auto text-[11px] font-mono text-[#73726e]">{choice.detail}</span>
              </label>
            );
          })}
        </div>
        {copyImages && (
          <p className="text-xs text-[#aeaaa4]">
            Images are <span className="font-mono">{MOVE_ATTACHMENTS_IMAGE_EXT.join(', ')}</span>.
          </p>
        )}
      </fieldset>
      )}

      {paths && identity?.sourceSystem === sourceSystem && (
        <div className="flex flex-col gap-1 text-xs text-[#73726e]">
          <span>
            Looks in{' '}
            <code className="font-mono text-[#37352f]">
              s3://{paths.sourceBucket}/{paths.sourcePrefix}
              {sourceSystem === 'jobadder'
                ? 'Backup/unzipped/'
                : isBullhorn
                  ? ''
                  : isThrive
                    ? 'unzipped/'
                    : 'export_data/'}
            </code>
          </span>
          {!imagesOnly && (
            <span>
              {isBullhorn ? 'People' : 'Documents'}{' '}
              <code className="font-mono text-[#37352f]">
                s3://{paths.destBucket}/{paths.destPrefix}
              </code>
            </span>
          )}
          {copyImages && (
            <span>
              {isBullhorn ? 'Companies' : 'Images'}{' '}
              <code className="font-mono text-[#37352f]">
                s3://{paths.destBucket}/{paths.imageDestPrefix}
              </code>
            </span>
          )}
          {isBullhorn && paths.certDestPrefix && (
            <span>
              Certifications{' '}
              <code className="font-mono text-[#37352f]">
                s3://{paths.destBucket}/{paths.certDestPrefix}
              </code>
            </span>
          )}
        </div>
      )}

      {pathProblem && (
        <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">
          {pathProblem}
        </p>
      )}
      {busy && (
        <p className="text-sm text-[#b5820b] bg-[#fbf3db] border border-[#f2e2b4] rounded px-3 py-2">
          A run is already using this destination. Wait for it to finish or cancel it.
        </p>
      )}
      {error && (
        <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2">
          {error}
        </p>
      )}
      {lastResult && (
        <p className="text-sm text-[#0f7b6c] bg-[#e5f5f2] border border-[#c5e8e1] rounded px-3 py-2">
          {lastResult}
        </p>
      )}

      <div className="flex justify-end pt-1">
        <button
          type="submit"
          disabled={
            loading ||
            !paths ||
            busy ||
            identity?.sourceSystem !== sourceSystem ||
            bullhornNeedsPassword
          }
          className="px-4 py-1.5 text-sm font-medium text-white bg-[#37352f] rounded hover:bg-[#2e2e2c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Starting…' : 'Dry run'}
        </button>
      </div>
        </>
      )}
    </form>
  );
}
